import {Provider} from '@angular/core';
import {CanMatchFn} from '@angular/router';
import {defer, of} from 'rxjs';
import {ClientService} from '../../../services/auth/client.service';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ConnectConnectionsService} from '../../../services/connect/connect-connections.service';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {ReferenceService} from '../../../services/enrich/reference.service';
import {RegistryService} from '../../../services/enrich/registry.service';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich/enrich-console-diagnostics.service';
import {EnrichSettings, HubspotProperty} from '../../../models/hubspot.model';
import {CompanySyncEligibility} from '../../../models/company-sync-eligibility.model';

export function isCompanySyncRulesFixtureUrl(href: string): boolean {
  const url = new URL(href);
  return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password &&
    ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) && url.pathname === '/enrich/settings' &&
    url.searchParams.getAll('enrichVisualFixture').length === 1 &&
    url.searchParams.get('enrichVisualFixture') === 'company-sync-rules';
}
export const companySyncRulesFixtureCanMatch: CanMatchFn = () => typeof window !== 'undefined' && isCompanySyncRulesFixtureUrl(window.location.href);
const properties: HubspotProperty[] = [
  {name: 'orgnr', label: 'Organization number', type: 'string', fieldType: 'text', groupName: 'companyinformation', automaticSyncWebhookSupported: true},
  {name: 'customer_type', label: 'Customer type', type: 'enumeration', fieldType: 'select', groupName: 'companyinformation', automaticSyncWebhookSupported: false,
    options: [{value: '001', label: 'Customer', hidden: false}, {value: '002', label: 'Prospect', hidden: false}]},
];
const key = 'shine-company-sync-rules-visual-fixture-v1';
const base = {id: 'fixture-settings', clientId: 'fixture-client', portalId: 12345, subscriptionActive: true, syncOnCompanyCreated: true,
  existingCompanySyncFrequency: 'weekly', addCompaniesMatchingFilters: false, filters: [], mapping: [], dataProviders: ['brreg'], dataSources: [],
  crmOrgNumberProperty: 'orgnr', crmSubUnitNumberProperty: '', lastUpdateTimestamp: '', announcementsTimelineSyncEnabled: false} as EnrichSettings;
const read = (): EnrichSettings => ({...base, ...JSON.parse(localStorage.getItem(key) ?? '{}')});
export const companySyncRulesFixtureProviders: Provider[] = [
  {provide: ClientService, useValue: {client: {id: 'fixture-client'}}},
  {provide: ClientLoadingService, useValue: {ensureClientSelected: () => of({id: 'fixture-client'})}},
  {provide: ConnectConnectionsService, useValue: {list: () => of([])}},
  {provide: RegistryService, useValue: {getStatus: () => of({providers: []})}},
  {provide: ReferenceService, useValue: {getHubspotProperties: () => of(properties)}},
  {provide: EnrichConsoleDiagnosticsService, useValue: {reportError: () => undefined}},
  {provide: HubspotService, useValue: {
    getSettings: () => defer(() => of(read())),
    updateSettings: (patch: Partial<EnrichSettings>) => defer(() => {
      const saved = {...read(), ...patch}; localStorage.setItem(key, JSON.stringify(saved)); return of(saved);
    }),
    previewCompanySyncEligibility: (policy: CompanySyncEligibility, _client: string, cursor?: string) => defer(() => {
      const all = policy.mode === 'all';
      return of({schemaVersion: 'company-sync-eligibility-preview.v1', policyRevision: 'fixture', sampled: true, pageSize: 3,
        counts: {eligible: all ? 3 : 1, excluded: all ? 0 : 1, pending: all ? 0 : 1, invalid: 0},
        items: [{id: '101', name: 'Synthetic eligible company', decision: 'eligible'},
          {id: '102', name: 'Synthetic excluded company', decision: all ? 'eligible' : 'excluded'},
          {id: '103', name: 'Synthetic missing information', decision: all ? 'eligible' : 'pending'}],
        nextCursor: cursor ? null : 'fixture-next-page', webhookCoverageMissing: []});
    }),
  }},
];
